import React, {memo, useCallback, useRef} from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import {alpha, SxProps, Theme} from '@mui/material/styles';
import {AuditProgramSectionVO} from '@/services/auditService/auditProgramService';
import {C134_PAGE} from '@/pages/components/audit-program/c-section/constants';
import ProgramSectionHeader from '@/pages/components/audit-program/a-section/ProgramSectionHeader';

export interface C134BudgetViewProps {
    header: AuditProgramSectionVO;
    values: Record<string, string>;
    onChange: (key: string, value: string) => void;
}

type C134Row = (typeof C134_PAGE.rows)[number];

const cellBorder = {borderColor: 'divider'};
const headSx = {
    ...cellBorder,
    fontWeight: 600,
    fontSize: '0.7rem',
    py: 0.75,
    px: 0.4,
    lineHeight: 1.25,
    verticalAlign: 'bottom' as const,
};
const bodySx = {
    ...cellBorder,
    py: 0.4,
    px: 0.4,
    verticalAlign: 'middle' as const,
    overflow: 'hidden',
};

const nativeBaseSx: SxProps<Theme> = {
    display: 'block',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    m: 0,
    border: '1px solid',
    borderColor: 'rgba(0, 0, 0, 0.23)',
    borderRadius: 1,
    px: 0.75,
    py: '4px',
    fontSize: (theme) => theme.typography.body2.fontSize,
    lineHeight: 1.25,
    fontFamily: 'inherit',
    color: 'text.primary',
    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
    outline: 'none',
    '&:hover': {borderColor: 'text.primary'},
    '&:focus': {
        borderColor: 'primary.main',
        boxShadow: (theme) => `0 0 0 1px ${theme.palette.primary.main}`,
    },
};

const CellInput = memo(function CellInput({
    value,
    onChange,
    align = 'left',
}: {
    value: string;
    onChange: (v: string) => void;
    align?: 'left' | 'center' | 'right';
}) {
    return (
        <Box
            component="input"
            value={value}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
            sx={{...nativeBaseSx, textAlign: align}}
        />
    );
});

const BudgetRow = memo(function BudgetRow({
    row,
    values,
    onChange,
}: {
    row: C134Row;
    values: Record<string, string>;
    onChange: (key: string, value: string) => void;
}) {
    const f = (k: string) => values[`${row.id}.${k}`] ?? '';
    const set = useCallback((k: string) => (v: string) => onChange(`${row.id}.${k}`, v), [onChange, row.id]);
    const bold = row.kind === 'total' || (row.kind === 'line' && row.name === 'TOTAL COST');

    if (row.kind === 'note') {
        return (
            <TableRow>
                <TableCell sx={bodySx} colSpan={3}>
                    <Typography variant="body2" color="text.secondary" sx={{fontStyle: 'italic'}}>
                        {row.name}
                    </Typography>
                </TableCell>
            </TableRow>
        );
    }

    if (row.kind === 'approve') {
        return (
            <TableRow>
                <TableCell sx={bodySx}>
                    <Typography variant="body2" fontWeight={600}>
                        {row.name}
                    </Typography>
                </TableCell>
                <TableCell sx={bodySx}>
                    <CellInput value={f('by')} onChange={set('by')}/>
                </TableCell>
                <TableCell sx={bodySx}>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        <Typography variant="caption" color="text.secondary" sx={{flexShrink: 0}}>
                            Date
                        </Typography>
                        <CellInput value={f('date')} onChange={set('date')} align="center"/>
                    </Box>
                </TableCell>
            </TableRow>
        );
    }

    if (row.kind === 'discuss') {
        return (
            <TableRow>
                <TableCell sx={bodySx}>
                    <Typography variant="body2" fontWeight={600}>
                        {row.name}
                    </Typography>
                </TableCell>
                <TableCell sx={bodySx}>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        <Typography variant="caption" color="text.secondary" sx={{flexShrink: 0}}>
                            Date
                        </Typography>
                        <CellInput value={f('date')} onChange={set('date')} align="center"/>
                    </Box>
                </TableCell>
                <TableCell sx={bodySx}>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        <Typography variant="caption" color="text.secondary" sx={{flexShrink: 0}}>
                            Fee agreed HK$
                        </Typography>
                        <CellInput value={f('fee')} onChange={set('fee')} align="right"/>
                    </Box>
                </TableCell>
            </TableRow>
        );
    }

    if (row.kind === 'lastYear') {
        return (
            <TableRow>
                <TableCell sx={bodySx}>
                    <Typography variant="body2" fontWeight={600}>
                        {row.name}
                    </Typography>
                </TableCell>
                <TableCell sx={bodySx}>
                    <CellInput value={f('cost')} onChange={set('cost')} align="right"/>
                </TableCell>
                <TableCell sx={bodySx}>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        <Typography variant="caption" color="text.secondary" sx={{flexShrink: 0}}>
                            Fee last year HK$
                        </Typography>
                        <CellInput value={f('fee')} onChange={set('fee')} align="right"/>
                    </Box>
                </TableCell>
            </TableRow>
        );
    }

    return (
        <TableRow>
            <TableCell sx={bodySx}>
                <Typography variant="body2" fontWeight={bold ? 700 : 400}>
                    {row.name}
                </Typography>
            </TableCell>
            <TableCell sx={bodySx}>
                <CellInput value={f('budget')} onChange={set('budget')} align="right"/>
            </TableCell>
            <TableCell sx={bodySx}>
                <CellInput value={f('actual')} onChange={set('actual')} align="right"/>
            </TableCell>
        </TableRow>
    );
});

/** Excel C13.4 — Budget and Performance Summary */
export default function C134BudgetView({header, values, onChange}: C134BudgetViewProps) {
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const stableOnChange = useCallback((key: string, value: string) => {
        onChangeRef.current(key, value);
    }, []);

    return (
        <Box sx={{p: 2}}>
            <ProgramSectionHeader
                header={header}
                variant="a13"
                refCode="C13.4"
                values={values}
                onChange={onChange}
            />

            <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{textAlign: 'center', letterSpacing: 0.4, mb: 1, mt: 0.5}}
            >
                {C134_PAGE.title}
            </Typography>

            <TableContainer sx={{overflowX: 'auto'}}>
                <Table
                    size="small"
                    sx={{
                        width: '100%',
                        minWidth: 720,
                        tableLayout: 'fixed',
                        borderCollapse: 'collapse',
                    }}
                >
                    <colgroup>
                        <col style={{width: '46%'}}/>
                        <col style={{width: '27%'}}/>
                        <col style={{width: '27%'}}/>
                    </colgroup>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headSx}/>
                            <TableCell sx={headSx} align="center">
                                {C134_PAGE.budgetHeader}
                            </TableCell>
                            <TableCell sx={headSx} align="center">
                                {C134_PAGE.actualHeader}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {C134_PAGE.rows.map((row) => (
                            <BudgetRow
                                key={row.id}
                                row={row}
                                values={values}
                                onChange={stableOnChange}
                            />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}
